/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Divider,
  FormControl,
  IconButton,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../../components/navigable_breadcrumb_link/styles";
import { RestaurantGroupContainer, StyledTitle, StyledTitleContainer } from "../restaurant_groups/styles";
import { FilterContainer } from "../../components/filter_container";
import { StyledButton, StyledInputLabel } from "../restaurant/styles.jsx";
import { UpdateButton } from "../../components/update_button";
import { Loader } from "../../components/loader";
import { IndexTable } from "../../components/index_table";
import DropdownSearchBar from "../../components/dorpdown_searchbar/index.jsx";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { StyledMenuItem } from "../../components/styled_menu_item/index.jsx";
import {
  buildEntityKey,
  describeMissingPartnerProgramFields,
  describeMissingPolicyFields,
  INITIAL_PARTNER_PROGRAM_CONFIGURATION,
  INITIAL_POLICY_CONFIGURATION,
  mapPartnerPolicyToConfiguration,
  mapPolicyToConfiguration,
  mergePrefillContract,
  PARTNER_PROGRAM_POLICY_TAB,
  programSidesToPartnership,
  PolicyEngineConfigurationModal,
  STANDARD_POLICY_TYPES,
} from "../../components/policy_engine_modal";
import { usePartnerProgramData } from "../../hooks/usePartnerProgramData";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import { selectBaseStyles, viewTypographyStyles } from "../reports/styles.js";
import { StatusContainer } from "./styles.jsx";
import api from "../../api/index.js";
import authenticatedFetch from "../../api/authenticatedFetch";
import camelize from "../../utils/camelize.js";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Query_Keys } from "../../lib/queryKeys.js";
import { PerPage } from "../../constants/perPage.js";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery.js";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { useDebounce } from "../../utils/useDebounce.js";
import { useNotificationContext } from "../../contexts/notificationContext";
import { buildQueryParams } from "../../utils/buildQueryParams.js";
import { resetPageAfterDeletion } from "../../utils/resetPageAfterDeletion";
import { useOtMutation } from "../../hooks/mutations/useOtMutation.js";
import { policyEngineMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/policyEngineMutationKeys.js";
import { useSelector } from "react-redux";
import { AbilityContext } from "../../contexts/abilityContext";
import { strictCanScope } from "../../utils/caslStrict";
import { checkAdmin } from "../../utils/checkAdmin";

// Permit.io action gating both dropdowns the create modal reads from: the
// endpoint authorizes `create` on the group (or on each targeted restaurant),
// so the options offered have to be scoped the same way.
const POLICY_CREATE_RESOURCE_ACTION = "create";

// Stable default for the modal dropdown queries. They stay disabled until the
// modal opens, so `data` is `undefined` on every render — an inline `[]` default
// would hand the option-cache effects a fresh array each time and spin them.
const EMPTY_DROPDOWN_OPTIONS = [];

// The two policy types a benefit program is filed under. Both sides carry a type as
// well as an id: a group id can collide with a restaurant id.
const PARTNERSHIP_POLICY_TYPES = ["provider", "recipient"];

// Policy types the edit modal can actually represent — it offers no card option, so
// a card policy would render with no type selected and its card name under a
// "Restaurant group" label. A program is represented by the partner tab, whose own
// layer control names the type.
const EDITABLE_POLICY_TYPES = [...STANDARD_POLICY_TYPES, ...PARTNERSHIP_POLICY_TYPES];

const POLICY_REQUIRED_FIELDS_ERROR = "Please fill the required fields";
const POLICY_CREATE_FALLBACK_ERROR = "Failed to create the policy";
const POLICY_ARCHIVE_FALLBACK_ERROR = "Failed to archive the policy";
const POLICY_UPDATE_FALLBACK_ERROR = "Failed to update the policy";
const ACTIVE_POLICY_CHECK_FALLBACK_ERROR = "Failed to check for an active policy";

// A recipient policy inherits from an ACTIVE provider, so the backend cascades rather
// than leaving one enforcing terms no longer in force.
const PROVIDER_ARCHIVE_CASCADE_NOTICE =
  "Archiving a provider policy will also archive its active and draft recipient policies.";

// Long enough that picking several restaurants in a row costs one request, short
// enough that the rules unlock as soon as the author stops choosing.
const ACTIVE_POLICY_CHECK_DEBOUNCE_MS = 300;

/**
 * @description Signature of the scope the create form currently targets, or an
 * empty string when nothing is selected yet. A string (rather than the params
 * object) is what the debounce and the query key hang off, so both change only
 * when the selection itself does — and the ids are sorted so re-picking the same
 * restaurants in another order is not treated as a new scope.
 * @param policyConfigurationData The current create-form state.
 * @returns The scope signature.
 */
const buildActivePolicyScopeKey = (policyConfigurationData) => {
  if (policyConfigurationData?.policy_type === "restaurant") {
    const restaurantIds = policyConfigurationData?.restaurant_ids || [];

    return restaurantIds.length ? `restaurant:${[...restaurantIds].sort().join(",")}` : "";
  }

  return policyConfigurationData?.restaurant_group_id
    ? `group:${policyConfigurationData.restaurant_group_id}`
    : "";
};

/**
 * @description Turns a scope signature back into the endpoint's params. Only the
 * key for the chosen policy type is sent: a restaurant policy derives its group
 * server-side, so the form has no group to check it against. The check is filtered
 * by the same Permit.io action the create request authorizes, so a scope the author
 * may file a policy in is never reported free just because they cannot read it.
 * @param scopeKey A signature built by buildActivePolicyScopeKey.
 * @returns The query params for GET /v1/admin/policy_engines/check_active_policy.
 */
const buildActivePolicyCheckParams = (scopeKey) => {
  const [scope, ids] = `${scopeKey}`.split(":");

  return scope === "restaurant"
    ? { restaurant_ids: ids.split(","), resource_action: POLICY_CREATE_RESOURCE_ACTION }
    : { restaurant_group_id: ids, resource_action: POLICY_CREATE_RESOURCE_ACTION };
};

const PROGRAM_PERMISSION_SUBJECTS = { RestaurantGroup: "Restaurant_Group", Restaurant: "Restaurant" };

const RESTAURANT_SIDE_TYPE = "Restaurant";

/**
 * @description Whether one side of a program is permitted. The backend lets a restaurant
 * side pass on its group's grant through the Permit.io resource hierarchy, but CASL is
 * built from the raw permission list and carries no such expansion — so the group grant
 * is accepted here by hand, the way this feature's own partnership dropdown does it.
 * @param ability The CASL ability.
 * @param side A `{ type, id, restaurant_group_id }` program side.
 * @param resourceAction The Permit.io action the endpoint authorizes.
 * @returns True when the admin holds the side itself or the group above it.
 */
const programSidePermits = (ability, side, resourceAction) => {
  const subject = PROGRAM_PERMISSION_SUBJECTS[side?.type];

  if (!subject || !side?.id) {
    return false;
  }

  if (strictCanScope(ability, resourceAction, subject, `${side.id}`)) {
    return true;
  }

  return side.type === RESTAURANT_SIDE_TYPE && Boolean(side.restaurant_group_id)
    && strictCanScope(ability, resourceAction, "Restaurant_Group", `${side.restaurant_group_id}`);
};

const EMPTY_CELL = "-";

/**
 * @description Names one side of a benefit program. A restaurant side is qualified the
 * way RestaurantGroupPartnership#pair_label qualifies it: the sides are polymorphic, so
 * a group and a restaurant can carry both the same id and the same name.
 * @param side A `{ type, id, name }` program side from the serializer.
 * @returns The display name, or an empty string when the side is unresolvable.
 */
const describeProgramSide = (side) => {
  if (!side?.name) {
    return "";
  }

  return side.type === RESTAURANT_SIDE_TYPE ? `${side.name} (${RESTAURANT_SIDE_TYPE})` : side.name;
};

/**
 * @description The DIRECTED pair a benefit program runs over. The provider states the
 * terms and the recipient receives them, so the arrow is the program itself — reversing
 * it would be another program, not another partnership. Empty for every other policy
 * type, whose scope the Source Name column already names on its own.
 * @param record An index row.
 * @returns "Provider → Recipient", or a dash.
 */
const describeProgram = (record) => {
  if (!PARTNERSHIP_POLICY_TYPES.includes(record?.policy_type)) {
    return EMPTY_CELL;
  }

  const provider = describeProgramSide(record?.partnership_provider);
  const recipient = describeProgramSide(record?.partnership_recipient);

  return provider && recipient ? `${provider} → ${recipient}` : EMPTY_CELL;
};

/**
 * @description The entity a policy's scope resolves to. On a benefit program that is the
 * RECIPIENT — which may be a group or a restaurant, while the Policy Type column reads
 * "Provider" / "Recipient" either way — so the type is qualified there and nowhere else.
 * @param record An index row.
 * @returns The source name, or a dash.
 */
const describeSourceName = (record) => {
  if (PARTNERSHIP_POLICY_TYPES.includes(record?.policy_type)) {
    return describeProgramSide(record?.source_name) || EMPTY_CELL;
  }

  return record?.source_name?.name || EMPTY_CELL;
};

/**
 * @description Whether the admin may act on a benefit program, matching the backend's
 * asymmetric check: a PROVIDER policy needs permission on both sides, because acting
 * on it retires the recipient's terms too; ARCHIVING a RECIPIENT policy needs the
 * recipient alone. Without this the button reads as enabled and the request comes back
 * 403. Only the archive narrows — `update` still asks for both sides, as
 * program_modification_entity_keys does.
 * @param ability The CASL ability.
 * @param record An index row.
 * @param resourceAction The Permit.io action the endpoint authorizes.
 * @returns True when every side the backend checks is permitted.
 */
const programSidesPermit = (ability, record, resourceAction) => {
  const recipientArchive = resourceAction === "create" && record?.policy_type === "recipient";
  const sides = recipientArchive
    ? [record?.partnership_recipient]
    : [record?.partnership_provider, record?.partnership_recipient];

  // Fails closed: a side the row does not fully name is a side that cannot be checked.
  return sides.every((side) => programSidePermits(ability, side, resourceAction));
};

/**
 * @description The shared form state an edit opens on, before the policy is fetched.
 * A program is filed under `provider` / `recipient`, neither of which is a tab — the
 * partner tab is, and its own layer control names the type.
 * @param record An index row.
 * @returns The initial shared form state for this row.
 */
const seedEditPolicyConfiguration = (record) => (
  PARTNERSHIP_POLICY_TYPES.includes(record?.policy_type)
    ? { ...INITIAL_POLICY_CONFIGURATION, policy_type: PARTNER_PROGRAM_POLICY_TAB }
    : INITIAL_POLICY_CONFIGURATION
);

/**
 * @description The partner form state an edit opens on, before the policy is fetched:
 * the layer and both sides, which the listing row already carries. The rest — the
 * description, the rules and the override metadata — arrives with the show response.
 * @param record An index row.
 * @returns The initial partner form state for this row.
 */
const seedEditPartnerProgram = (record) => {
  if (!PARTNERSHIP_POLICY_TYPES.includes(record?.policy_type)) {
    return INITIAL_PARTNER_PROGRAM_CONFIGURATION;
  }

  return {
    ...INITIAL_PARTNER_PROGRAM_CONFIGURATION,
    policy_layer: record.policy_type,
    partnership: programSidesToPartnership(record),
    provider_key: buildEntityKey(record?.partnership_provider?.type, record?.partnership_provider?.id),
    recipient_key: buildEntityKey(record?.partnership_recipient?.type, record?.partnership_recipient?.id),
  };
};

/**
 * @description Message for a failed active-policy check. The 422 the endpoint
 * raises on a conflict carries both the reason and the occupied scope names, so
 * the author is told which selection is the problem.
 * @param error The axios error raised by the check.
 * @returns The message to surface in the snackbar.
 */
const buildActivePolicyCheckErrorMessage = (error) => {
  const responseData = error?.response?.data;
  const message = typeof responseData?.error === "string" ? responseData.error.trim() : "";

  if (!message) {
    return ACTIVE_POLICY_CHECK_FALLBACK_ERROR;
  }

  const names = (Array.isArray(responseData?.names) ? responseData.names : []).filter(Boolean);

  return names.length ? `${message}: ${names.join(", ")}` : message;
};

const humanizePolicyErrorKey = (key) => {
  return `${key}`
    .replace(/_ids$/, "s")
    .replace(/_id$/, "")
    .replace(/_/g, " ")
    .replace(/^./, (character) => character.toUpperCase());
};

/**
 * @description Flattens the `{ attribute: [message] }` error payload the policy
 * engine endpoints return into a single readable sentence, so every backend
 * validation reaches the author instead of only the first one.
 * @param error The axios error raised by the request.
 * @param fallbackMessage The message to use when the payload carries nothing readable.
 * @returns The message to surface in the snackbar.
 */
const buildPolicyErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const responseErrors = responseData?.errors;

  if (typeof responseErrors === "string") {
    return responseErrors;
  }

  if (responseErrors && typeof responseErrors === "object") {
    const messages = Object.entries(responseErrors).flatMap(([key, value]) => {
      const valueList = Array.isArray(value) ? value : [value];

      return valueList
        .filter((message) => typeof message === "string" && message.trim())
        // `base` errors already read as full sentences; attribute errors are bare
        // fragments ("must not contain duplicates") and need their subject back.
        .map((message) => (key === "base" ? message : `${humanizePolicyErrorKey(key)} ${message}`));
    });

    if (messages.length) {
      return messages.join(". ");
    }
  }

  if (typeof responseData?.error === "string") {
    return responseData.error;
  }

  return fallbackMessage;
};

export const PolicyEngine = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyConfigurationData, setPolicyConfigurationData] = useState(INITIAL_POLICY_CONFIGURATION);
  // The program form's own state. Held apart from the scope form's so neither can
  // leak a field into the other's payload; only the name and draft flag are shared.
  const [partnerProgramData, setPartnerProgramData] = useState(INITIAL_PARTNER_PROGRAM_CONFIGURATION);
  const [policyRestaurantGroupSearch, setPolicyRestaurantGroupSearch] = useState("");
  const [policyRestaurantGroupOptionsCache, setPolicyRestaurantGroupOptionsCache] = useState([]);
  const [policyRestaurantSearch, setPolicyRestaurantSearch] = useState("");
  const [policyRestaurantOptionsCache, setPolicyRestaurantOptionsCache] = useState([]);
  const [manualUserSearch, setManualUserSearch] = useState("");
  const [manualUserOptionsCache, setManualUserOptionsCache] = useState([]);
  const [archiveTargetPolicy, setArchiveTargetPolicy] = useState(null);
  const [editTargetPolicy, setEditTargetPolicy] = useState(null);
  const restaurantGroupSearchResetTimeoutRef = useRef(null);
  const policyRestaurantGroupSearchResetTimeoutRef = useRef(null);
  const policyRestaurantSearchResetTimeoutRef = useRef(null);
  const manualUserSearchResetTimeoutRef = useRef(null);
  const singleRestaurantGroupAutoApplied = useRef(false);
  const editPolicySeededIdRef = useRef(null);
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedPolicyRestaurantGroupSearch = useDebounce(policyRestaurantGroupSearch.trim(), 300);
  const debouncedPolicyRestaurantSearch = useDebounce(policyRestaurantSearch.trim(), 300);
  const debouncedManualUserSearch = useDebounce(manualUserSearch.trim(), 300);
  const showNotification = useNotificationContext();
  const { ability } = useContext(AbilityContext) || {};
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles);
  // Unscoped check: the button only opens the modal, and the scope the policy will
  // be filed under is chosen inside it — each row action gates on its own scope.
  const canCreatePolicy = Boolean(
    ability?.can("create", "Restaurant_Group") || ability?.can("create", "Restaurant")
  );
  // The modal is shared by both flows; the policy being edited is what tells them
  // apart. Editing cannot move a policy's scope, so the create-scoped dropdowns
  // the modal reads from are left unqueried in that mode.
  const isEditPolicyMode = Boolean(editTargetPolicy);
  const isPartnerProgramTab = policyConfigurationData?.policy_type === PARTNER_PROGRAM_POLICY_TAB;
  const scopeDropdownsEnabled = policyModalOpen && !isEditPolicyMode && !isPartnerProgramTab;

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = EMPTY_DROPDOWN_OPTIONS,
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, restaurantGroups, "label", ["value"])
    ));
  }, [restaurantGroups]);

  const fetchPolicyRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/permission_restaurant_group_list", {
      params: {
        resource_action: POLICY_CREATE_RESOURCE_ACTION,
        page: pageParam,
        per_page: PerPage,
        search: debouncedPolicyRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map((group) => ({ id: group?.value, name: group?.label })) || [];
  };

  const {
    data: policyRestaurantGroups = EMPTY_DROPDOWN_OPTIONS,
    isFetching: policyRestaurantGroupFetching,
    isFetchingNextPage: policyRestaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextPolicyRestaurantGroupsPage,
    hasNextPage: hasNextPolicyRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.permission_restaurant_group({
      resource_action: POLICY_CREATE_RESOURCE_ACTION,
      search: debouncedPolicyRestaurantGroupSearch,
    }),
    queryFn: fetchPolicyRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: scopeDropdownsEnabled,
  });

  const {
    rootRef: policyRestaurantGroupMenuRef,
    targetRef: policyRestaurantGroupLoaderRef,
  } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextPolicyRestaurantGroupsPage,
    hasNextPage: hasNextPolicyRestaurantGroupsPage,
    isFetchingNextPage: policyRestaurantGroupsFetchingNextPage,
  });

  // Not scoped by restaurant group: a restaurant-level policy is scoped by its
  // restaurants alone and the backend derives the group from them, so the two
  // filters are independent and only one is ever shown at a time.
  const fetchPolicyRestaurantList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        resource_action: POLICY_CREATE_RESOURCE_ACTION,
        page: pageParam,
        per_page: PerPage,
        search: debouncedPolicyRestaurantSearch || undefined,
      },
    });
    return response?.data?.map((restaurant) => ({ id: restaurant?.value, name: restaurant?.label })) || [];
  };

  const {
    data: policyRestaurants = EMPTY_DROPDOWN_OPTIONS,
    isFetching: policyRestaurantFetching,
    isFetchingNextPage: policyRestaurantsFetchingNextPage,
    fetchNextPage: fetchNextPolicyRestaurantsPage,
    hasNextPage: hasNextPolicyRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      resource_action: POLICY_CREATE_RESOURCE_ACTION,
      search: debouncedPolicyRestaurantSearch,
    }),
    queryFn: fetchPolicyRestaurantList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: scopeDropdownsEnabled,
  });

  const {
    rootRef: policyRestaurantMenuRef,
    targetRef: policyRestaurantLoaderRef,
  } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextPolicyRestaurantsPage,
    hasNextPage: hasNextPolicyRestaurantsPage,
    isFetchingNextPage: policyRestaurantsFetchingNextPage,
  });

  // The scope the excepted-users list is drawn from. Unlike the scope dropdowns
  // this stays live in edit mode — exceptions remain editable after create — so
  // the scope comes from the form state, which mapPolicyToConfiguration seeds
  // from the persisted policy.
  const exceptionScope = useMemo(() => {
    if (policyConfigurationData?.policy_type === "restaurant") {
      return { restaurant_ids: policyConfigurationData?.restaurant_ids || [] };
    }

    return { restaurant_group_id: policyConfigurationData?.restaurant_group_id || "" };
  }, [
    policyConfigurationData?.policy_type,
    policyConfigurationData?.restaurant_ids,
    policyConfigurationData?.restaurant_group_id,
  ]);

  const hasExceptionScope = Boolean(
    exceptionScope?.restaurant_group_id || exceptionScope?.restaurant_ids?.length
  );
  const exceptionScopeKey = JSON.stringify(exceptionScope);

  // Users picked under a previous scope are no longer in scope, and the endpoint
  // rejects the rule when they are not. Create mode only: an edit cannot move its
  // scope, and running there would wipe the ids the seed just loaded.
  useEffect(() => {
    if (isEditPolicyMode) {
      return;
    }

    setPolicyConfigurationData((currentData) => (
      currentData?.exception_network_guest_ids?.length
        ? { ...currentData, exception_network_guest_ids: [] }
        : currentData
    ));
  }, [exceptionScopeKey, isEditPolicyMode]);

  const fetchManualUsersList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/manual_users_list", {
      params: {
        ...exceptionScope,
        page: pageParam,
        per_page: PerPage,
        search: debouncedManualUserSearch || undefined,
      },
    });
    return response?.data?.map((user) => ({ id: user?.value, name: user?.label })) || [];
  };

  const {
    data: manualUsers = EMPTY_DROPDOWN_OPTIONS,
    isFetching: manualUsersFetching,
    isFetchingNextPage: manualUsersFetchingNextPage,
    fetchNextPage: fetchNextManualUsersPage,
    hasNextPage: hasNextManualUsersPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.manual_users_dropdown({
      ...exceptionScope,
      search: debouncedManualUserSearch,
    }),
    queryFn: fetchManualUsersList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: policyModalOpen && hasExceptionScope,
  });

  const {
    rootRef: manualUserMenuRef,
    targetRef: manualUserLoaderRef,
  } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextManualUsersPage,
    hasNextPage: hasNextManualUsersPage,
    isFetchingNextPage: manualUsersFetchingNextPage,
  });

  // mergeOptionsIfChanged, not mergeUniqueByKey: it returns the current array
  // reference when the merge is a no-op, so a re-run cannot re-trigger a render.
  useEffect(() => {
    setPolicyRestaurantGroupOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, policyRestaurantGroups, "id", ["name"])
    ));
  }, [policyRestaurantGroups]);

  useEffect(() => {
    setManualUserOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, manualUsers, "id", ["name"])
    ));
  }, [manualUsers]);

  // Keyed off the PERSISTED ids, not the live selection: the live one changes on
  // every click and would refetch each time, while the persisted set is what the
  // paginated list may not cover. Empty outside edit mode.
  const persistedExceptionUserIds = useMemo(() => {
    const rule = (policyConfigurationData?.persisted_rules || []).find(
      (entry) => entry?.rule_key === "exception_network_guest_ids"
    );

    return Array.isArray(rule?.rule_value) ? rule.rule_value : [];
  }, [policyConfigurationData?.persisted_rules]);

  // Without this an edited policy renders its excepted users as bare ids until
  // the page containing them happens to load.
  const { data: hydratedExceptionUsers = EMPTY_DROPDOWN_OPTIONS } = useQuery({
    queryKey: Query_Keys.manual_users_dropdown({
      ...exceptionScope,
      network_guest_ids: persistedExceptionUserIds,
    }),
    queryFn: async () => {
      const response = await api.get("/v1/admin/dropdowns/manual_users_list", {
        params: {
          ...exceptionScope,
          network_guest_ids: persistedExceptionUserIds,
          per_page: PerPage,
        },
      });
      return response?.data?.map((user) => ({ id: user?.value, name: user?.label })) || [];
    },
    staleTime: 10 * 60 * 1000,
    enabled: policyModalOpen && hasExceptionScope && persistedExceptionUserIds.length > 0,
  });

  useEffect(() => {
    setManualUserOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, hydratedExceptionUsers, "id", ["name"])
    ));
  }, [hydratedExceptionUsers]);

  useEffect(() => {
    setPolicyRestaurantOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, policyRestaurants, "id", ["name"])
    ));
  }, [policyRestaurants]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroups.length === 1;

  useEffect(() => {
    if (filters?.restaurant_group_id || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return;
    }

    const id = restaurantGroups?.[0]?.label;

    singleRestaurantGroupAutoApplied.current = true;
    setFilters({ ...filters, restaurant_group_id: id });
    setAppliedFilters({ restaurant_group_id: id });
    setFiltersChanged(false);
  }, [filters, hasSingleAvailableRestaurantGroup, restaurantGroups]);

  const fetchPolicyEngines = async () => {
    const activeRestaurantGroupId = appliedFilters?.restaurant_group_id;
    if (!activeRestaurantGroupId) {
      return [];
    }

    const response = await api.get("/v1/admin/policy_engines", {
      params: {
        restaurant_group_id: activeRestaurantGroupId,
        per_page: itemsPerPage,
        page: page,
        sort_by: sortBy,
        sort_order: sortOrder,
      },
    });
    return { data: response?.data, pageCount: response?.headers["total-count"] };
  };

  const { data: policyEngines, isLoading, isFetching } = useQuery({
    queryKey: Query_Keys.policy_engines({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchPolicyEngines,
    staleTime: 10 * 60 * 1000,
  });

  const { data: editPolicy, isError: editPolicyFailed } = useQuery({
    queryKey: Query_Keys.show_policy_engine({ id: editTargetPolicy?.id }),
    queryFn: async () => {
      const response = await api.get(`/v1/admin/policy_engines/${editTargetPolicy?.id}`);
      return response?.data;
    },
    enabled: isEditPolicyMode,
  });

  useEffect(() => {
    if (!editPolicy || editPolicySeededIdRef.current === editPolicy?.id) {
      return;
    }

    editPolicySeededIdRef.current = editPolicy?.id;
    setPolicyConfigurationData(mapPolicyToConfiguration(editPolicy, editTargetPolicy?.source_name?.name));

    // A program needs both states seeded: the shell selects the partner tab off
    // `policy_type`, and "provider" is neither a standard tab nor the partner one —
    // it would render no form body at all. The real layer rides on the partner state.
    if (!PARTNERSHIP_POLICY_TYPES.includes(editPolicy?.policy_type)) {
      return;
    }

    setPolicyConfigurationData((currentData) => ({ ...currentData, policy_type: PARTNER_PROGRAM_POLICY_TAB }));
    // The pair is seeded at click time, so a recipient's contract can land BEFORE the
    // policy does. Re-apply it over the seeded rules rather than dropping it: the
    // prefill seeds once per contract and would not run again.
    setPartnerProgramData((currentData) => {
      const seeded = mapPartnerPolicyToConfiguration(editPolicy);

      return currentData?.prefill ? mergePrefillContract(currentData.prefill, seeded) : seeded;
    });
  }, [editPolicy, editTargetPolicy]);

  // A live policy can only occupy a free scope, so the create flow checks the
  // selection up front instead of letting the author fill the rules in and meet
  // the 422 on submit. A draft occupies nothing and an edit cannot move the scope
  // it has, so neither runs the check.
  const activePolicyCheckRequired = policyModalOpen && !isEditPolicyMode && !isPartnerProgramTab
    && !policyConfigurationData?.draft;
  const activePolicyScopeKey = activePolicyCheckRequired ? buildActivePolicyScopeKey(policyConfigurationData) : "";
  const debouncedActivePolicyScopeKey = useDebounce(activePolicyScopeKey, ACTIVE_POLICY_CHECK_DEBOUNCE_MS);
  // The debounced key trails the live one for a beat after every change, and
  // firing on it then would check a scope the author has already moved off.
  const activePolicyCheckEnabled = Boolean(debouncedActivePolicyScopeKey)
    && debouncedActivePolicyScopeKey === activePolicyScopeKey;

  const {
    isPending: activePolicyCheckPending,
    isFetching: activePolicyChecking,
    isSuccess: activePolicyCheckPassed,
    error: activePolicyCheckError,
  } = useQuery({
    queryKey: Query_Keys.check_active_policy({ scope: debouncedActivePolicyScopeKey }),
    queryFn: async () => {
      const response = await api.get("/v1/admin/policy_engines/check_active_policy", {
        params: buildActivePolicyCheckParams(debouncedActivePolicyScopeKey),
      });
      return response?.data;
    },
    enabled: activePolicyCheckEnabled,
  });

  // The conflict itself is a 422, so it arrives as the query's error rather than
  // its data — it is the one outcome reported in the snackbar instead of inline.
  const activePolicyCheckMessage = activePolicyCheckError
    ? buildActivePolicyCheckErrorMessage(activePolicyCheckError)
    : "";

  useEffect(() => {
    if (!activePolicyCheckMessage) {
      return;
    }

    showNotification(activePolicyCheckMessage, "error");
    // showNotification is rebuilt on every provider render, so it stays out of the
    // deps; the scope key is what re-announces the same conflict after the author
    // moves to another scope and back.
  }, [activePolicyCheckMessage, debouncedActivePolicyScopeKey]);

  const activePolicyCheckStatus = {
    isChecking: Boolean(activePolicyScopeKey)
      && (!activePolicyCheckEnabled || activePolicyChecking || activePolicyCheckPending),
    isClear: activePolicyCheckEnabled && !activePolicyChecking && activePolicyCheckPassed,
  };

  const {
    partnershipDropdown,
    benefitCardsDropdown,
    audienceCardsDropdown,
    programExceptionUsersDropdown,
    audienceRestaurantsDropdown,
    audienceJobsDropdown,
    partnerProgramCheck,
    partnerProgramPrefill,
    audiencePreview,
  } = usePartnerProgramData({
    enabled: policyModalOpen && isPartnerProgramTab,
    isDraft: Boolean(policyConfigurationData?.draft),
    isEditMode: isEditPolicyMode,
    partnerProgramData,
    setPartnerProgramData,
  });

  // Same inversion as the scope check: the conflict is a 422, so it arrives as the
  // query's error rather than its data.
  const partnerProgramCheckMessage = partnerProgramCheck?.error
    ? buildActivePolicyCheckErrorMessage(partnerProgramCheck.error)
    : "";

  useEffect(() => {
    if (!partnerProgramCheckMessage) {
      return;
    }

    // showNotification is rebuilt on every provider render, so it stays out of the deps.
    showNotification(partnerProgramCheckMessage, "error");
  }, [partnerProgramCheckMessage]);

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => String(res?.label) === String(id));
    return restaurantGroup ? restaurantGroup?.value : "";
  };

  const handleRestaurantGroupChange = (event) => {
    setFilters((prev) => ({ ...prev, restaurant_group_id: event.target.value }));
    setFiltersChanged(true);
  };

  const handleRestaurantGroupSearchClose = () => {
    if (restaurantGroupSearchResetTimeoutRef.current) {
      window.clearTimeout(restaurantGroupSearchResetTimeoutRef.current);
    }

    restaurantGroupSearchResetTimeoutRef.current = window.setTimeout(() => {
      setRestaurantGroupSearch("");
      restaurantGroupSearchResetTimeoutRef.current = null;
    }, 1000);
  };

  // Both modal dropdowns keep their search term for a beat after the menu closes so
  // the rendered selection does not flicker while the reset query settles.
  const deferSearchReset = (timeoutRef, resetSearch) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      resetSearch("");
      timeoutRef.current = null;
    }, 1000);
  };

  const handlePolicyRestaurantGroupSearchClose = () => {
    deferSearchReset(policyRestaurantGroupSearchResetTimeoutRef, setPolicyRestaurantGroupSearch);
  };

  const handlePolicyRestaurantSearchClose = () => {
    deferSearchReset(policyRestaurantSearchResetTimeoutRef, setPolicyRestaurantSearch);
  };

  const handleManualUserSearchClose = () => {
    deferSearchReset(manualUserSearchResetTimeoutRef, setManualUserSearch);
  };

  useEffect(() => () => {
    [
      restaurantGroupSearchResetTimeoutRef,
      policyRestaurantGroupSearchResetTimeoutRef,
      policyRestaurantSearchResetTimeoutRef,
      manualUserSearchResetTimeoutRef,
    ].forEach((timeoutRef) => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    });
  }, []);

  // Deliberately not prefilled from the page filter: that dropdown lists every
  // group the admin can read, while the modal may only offer the ones they can
  // create in, so a prefill could seed a group the create request would reject.
  const handlePolicyModalOpen = () => {
    setEditTargetPolicy(null);
    setPolicyConfigurationData(INITIAL_POLICY_CONFIGURATION);
    setPartnerProgramData(INITIAL_PARTNER_PROGRAM_CONFIGURATION);
    setPolicyModalOpen(true);
  };

  const handlePolicyEditOpen = (record) => {
    // The tab, the layer and both program sides come off the LISTING row, which
    // already names them — waiting for the show response would open a program on the
    // Group tab and swap the form (and the dialog's width) out from under the reader.
    // Only the description, the rules and the status still have to be fetched.
    setPolicyConfigurationData(seedEditPolicyConfiguration(record));
    setPartnerProgramData(seedEditPartnerProgram(record));
    editPolicySeededIdRef.current = null;
    setEditTargetPolicy(record);
    setPolicyModalOpen(true);
  };

  const handlePolicyModalClose = () => {
    setPolicyModalOpen(false);
    setEditTargetPolicy(null);
    editPolicySeededIdRef.current = null;
    setPolicyConfigurationData(INITIAL_POLICY_CONFIGURATION);
    setPartnerProgramData(INITIAL_PARTNER_PROGRAM_CONFIGURATION);
    setPolicyRestaurantGroupSearch("");
    setPolicyRestaurantSearch("");
    setManualUserSearch("");
    setManualUserOptionsCache([]);
  };

  useEffect(() => {
    if (!editPolicyFailed) {
      return;
    }

    showNotification("Failed to load the policy", "error");
    handlePolicyModalClose();
  }, [editPolicyFailed]);

  const { mutate: createPolicyEngine, isPending: policyCreating } = useOtMutation({
    mutationFn: (payload) => api.post("/v1/admin/policy_engines", payload),
    invalidateKeys: policyEngineMutationKeyGroups.create,
    successMessage: "Policy created successfully",
    errorMessage: ({ error }) => buildPolicyErrorMessage(error, POLICY_CREATE_FALLBACK_ERROR),
    onSuccess: () => {
      handlePolicyModalClose();
    },
  });

  const { mutate: updatePolicyEngine, isPending: policyUpdating } = useOtMutation({
    mutationFn: (payload) => api.put(`/v1/admin/policy_engines/${editTargetPolicy?.id}`, payload),
    invalidateKeys: policyEngineMutationKeyGroups.update,
    successMessage: "Policy updated successfully",
    errorMessage: ({ error }) => buildPolicyErrorMessage(error, POLICY_UPDATE_FALLBACK_ERROR),
    onSuccess: () => {
      handlePolicyModalClose();
    },
  });

  const { mutate: archivePolicyEngine, isPending: policyArchiving } = useOtMutation({
    mutationFn: (policyId) => api.post(`/v1/admin/policy_engines/${policyId}/archive`),
    invalidateKeys: policyEngineMutationKeyGroups.archive,
    successMessage: "Policy archived successfully",
    errorMessage: ({ error }) => buildPolicyErrorMessage(error, POLICY_ARCHIVE_FALLBACK_ERROR),
    onSuccess: () => {
      resetPageAfterDeletion({
        totalCount: policyEngines?.pageCount,
        removed: 1,
        page,
        itemsPerPage,
        setPage,
      });
    },
    onSettled: () => {
      setArchiveTargetPolicy(null);
    },
  });

  /**
   * @description Announces what the author has to fix. The rule controls report
   * themselves with an outline rather than a message, so the snackbar is where
   * their problems are actually named. Returns whether anything was reported, so a
   * caller can use it as its own gate.
   * @param missingFields Blank required fields, by form label.
   * @param ruleMessages Rule problems that no field renders a message for.
   * @returns True when something was wrong and a message was shown.
   */
  const reportPolicyValidationErrors = ({ missingFields = [], ruleMessages = [] } = {}) => {
    const messages = [
      ...(missingFields.length ? [`${POLICY_REQUIRED_FIELDS_ERROR}: ${missingFields.join(", ")}`] : []),
      ...ruleMessages,
    ];

    if (!messages.length) {
      return false;
    }

    showNotification(messages.join(". "), "error");
    return true;
  };

  // The modal gates its own submit on the same helper, so this is the last check
  // before either request rather than the only one — it keeps an incomplete rule
  // set from reaching the endpoint whichever path calls in.
  const handlePolicySubmit = (payload) => {
    const missingFields = isPartnerProgramTab
      ? describeMissingPartnerProgramFields(policyConfigurationData, partnerProgramData, isEditPolicyMode)
      : describeMissingPolicyFields(policyConfigurationData);

    if (reportPolicyValidationErrors({ missingFields })) {
      return;
    }

    if (isEditPolicyMode) {
      updatePolicyEngine(payload);
      return;
    }

    createPolicyEngine(payload);
  };

  const handleArchiveDialogClose = () => {
    setArchiveTargetPolicy(null);
  };

  const handleArchiveConfirm = () => {
    if (!archiveTargetPolicy?.id) {
      return;
    }

    archivePolicyEngine(archiveTargetPolicy.id);
  };

  const handleFilterChange = () => {
    setAppliedFilters(filters);
    setItemsPerPage(25);
    setPage(1);
    setFiltersChanged(false);
  };

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const handleExportCsv = useCallback(async () => {
    try {
      setCsvLoading(true);
      const exportParams = { ...appliedFilters };
      const query = buildQueryParams(exportParams);
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=policy_engines&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: "GET",
        defaultErrorMessage: "Failed to download CSV",
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `policy_engines_${date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      showNotification("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("CSV download error:", error);
      showNotification(error?.message || "Failed to download CSV", "error");
    } finally {
      setCsvLoading(false);
    }
  }, [appliedFilters, showNotification]);

  // `program` is derived from two polymorphic, FK-less columns, so the endpoint's
  // sort_by whitelist has nothing to order it by — it stays unsortable.
  const TableColumns = [
    { key: "id", sortable: true },
    { key: "name", sortable: true },
    { key: "policy_type", sortable: true },
    { key: "source_name", sortable: true },
    { key: "inherits_from", sortable: true },
    { key: "program", sortable: false },
    { key: "version", sortable: true },
    { key: "status", sortable: true },
    { key: "actions", sortable: false },
  ];

  const columns = TableColumns?.map((column) => ({
    key: column.key,
    label: camelize(column.key),
    sortable: column.sortable,
  }));

  const TableRows = (data) => {
    return data?.map((record) => {
      // Both row actions gate on the policy's own scope — a restaurant override on
      // its restaurant, everything else on its group — but each on the Permit.io
      // action its endpoint actually authorizes: `update` for edit, `create` for
      // archive (POLICY_MODIFICATION_ACTIONS in the policy engines controller).
      const scopePermits = (resourceAction) => {
        if (superAdmin?.role === "admin") {
          return true;
        }

        if (PARTNERSHIP_POLICY_TYPES.includes(record?.policy_type)) {
          return programSidesPermit(ability, record, resourceAction);
        }

        // An override goes through the same group fallback its restaurant would as a
        // program side. The branch already knows the type, so it states it rather than
        // reading it off the row.
        return record?.policy_type === "restaurant"
          ? programSidePermits(ability, { ...record?.source_name, type: RESTAURANT_SIDE_TYPE }, resourceAction)
          : strictCanScope(ability, resourceAction, "Restaurant_Group", `${record?.restaurant_group?.id}`);
      };

      const editSupported = EDITABLE_POLICY_TYPES.includes(record?.policy_type);
      const editPermitted = editSupported && scopePermits("update");
      const archivePermitted = scopePermits("create");

      return {
        id: record?.id,
        name: record?.name,
        policy_type: camelize(record?.policy_type),
        source_name: describeSourceName(record),
        inherits_from: record?.inherits_from || EMPTY_CELL,
        program: describeProgram(record),
        version: record?.version,
        status: (
          <StatusContainer status={record?.status}>{camelize(record?.status)}</StatusContainer>
        ),
        actions: (
          <Box sx={{ display: "flex" }}>
            {editSupported && (
              <Tooltip title="Edit Policy" arrow>
                <span>
                  <IconButton
                    disabled={!editPermitted}
                    onClick={() => handlePolicyEditOpen(record)}
                    data-testid={`edit-policy-button-${record?.id}`}
                  >
                    <ModeEditIcon
                      sx={{
                        color: editPermitted ? "#707070" : "#e0e0e0",
                        cursor: editPermitted ? "pointer" : "default",
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            <Tooltip title="Archive Policy" arrow>
              <span>
                <IconButton
                  disabled={!archivePermitted}
                  onClick={() => setArchiveTargetPolicy(record)}
                  data-testid={`archive-policy-button-${record?.id}`}
                >
                  <DeleteIcon
                    sx={{
                      color: archivePermitted ? "#707070" : "#e0e0e0",
                      cursor: archivePermitted ? "pointer" : "default",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      };
    });
  };

  const rows = TableRows(policyEngines?.data);

  return (
    <RestaurantGroupContainer>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Policy Engine</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <FilterContainer>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: "18px" }}>
            <Typography
              variant="body2"
              sx={{
                ...viewTypographyStyles,
                alignSelf: { xs: "flex-start", sm: "center" },
                whiteSpace: "nowrap",
                mt: 0,
              }}
            >
              View
            </Typography>

            <FormControl fullWidth size="small">
              <StyledInputLabel>Restaurant Group</StyledInputLabel>
              <Select
                value={filters?.restaurant_group_id ? filters.restaurant_group_id : ""}
                renderValue={() => getRestaurantGroupName(filters?.restaurant_group_id)}
                size="small"
                label="Restaurant Group"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: 120,
                  width: 160,
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantGroupChange}
                onClose={handleRestaurantGroupSearchClose}
                data-testid="policy-engine-restaurant-group-select"
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantGroupSearch}
                  placeholder={"Search restaurant group"}
                  isFetching={restaurantGroupFetching}
                />
                {restaurantGroups?.map((res) => (
                  <StyledMenuItem key={res?.label} value={res?.label}>
                    {filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </FormControl>
          </Box>

          <UpdateButton
            onClick={handleFilterChange}
            filtersChanged={filtersChanged}
            status={isFetching}
          />
        </Box>
      </FilterContainer>

      <StyledTitleContainer>
        <StyledTitle>Policy Engine</StyledTitle>
      </StyledTitleContainer>

      <Divider
        style={{
          borderStyle: "solid",
          borderWidth: "1px",
        }}
      />

      <div style={{ padding: "25px 0px", display: "flex", gap: "20px" }}>
        <StyledButton
          variant="contained"
          onClick={handlePolicyModalOpen}
          startIcon={<AddIcon />}
          data-testid="policy-engine-create-btn"
          disabled={!canCreatePolicy}
        >
          Create Policy
        </StyledButton>
      </div>

      <PolicyEngineConfigurationModal
        open={policyModalOpen}
        onClose={handlePolicyModalClose}
        policyConfigurationData={policyConfigurationData}
        setPolicyConfigurationData={setPolicyConfigurationData}
        onSubmit={handlePolicySubmit}
        onValidationError={reportPolicyValidationErrors}
        isSubmitting={isEditPolicyMode ? policyUpdating : policyCreating}
        mode={isEditPolicyMode ? "edit" : "create"}
        isLoading={isEditPolicyMode && !editPolicy}
        activePolicyCheck={activePolicyCheckStatus}
        restaurantGroupDropdown={{
          options: policyRestaurantGroups,
          optionsCache: policyRestaurantGroupOptionsCache,
          isFetching: policyRestaurantGroupFetching,
          hasNextPage: hasNextPolicyRestaurantGroupsPage,
          menuRef: policyRestaurantGroupMenuRef,
          loaderRef: policyRestaurantGroupLoaderRef,
          onSearchChange: setPolicyRestaurantGroupSearch,
          onMenuClose: handlePolicyRestaurantGroupSearchClose,
        }}
        restaurantDropdown={{
          options: policyRestaurants,
          optionsCache: policyRestaurantOptionsCache,
          isFetching: policyRestaurantFetching,
          hasNextPage: hasNextPolicyRestaurantsPage,
          menuRef: policyRestaurantMenuRef,
          loaderRef: policyRestaurantLoaderRef,
          onSearchChange: setPolicyRestaurantSearch,
          onMenuClose: handlePolicyRestaurantSearchClose,
        }}
        manualUsersDropdown={{
          options: manualUsers,
          optionsCache: manualUserOptionsCache,
          isFetching: manualUsersFetching,
          hasNextPage: hasNextManualUsersPage,
          menuRef: manualUserMenuRef,
          loaderRef: manualUserLoaderRef,
          onSearchChange: setManualUserSearch,
          onMenuClose: handleManualUserSearchClose,
        }}
        partnerProgramData={partnerProgramData}
        setPartnerProgramData={setPartnerProgramData}
        partnerProgramCheck={partnerProgramCheck}
        partnerProgramPrefill={partnerProgramPrefill}
        partnershipDropdown={partnershipDropdown}
        benefitCardsDropdown={benefitCardsDropdown}
        programExceptionUsersDropdown={programExceptionUsersDropdown}
        audienceRestaurantsDropdown={audienceRestaurantsDropdown}
        audienceCardsDropdown={audienceCardsDropdown}
        audienceJobsDropdown={audienceJobsDropdown}
        audiencePreview={audiencePreview}
      />

      <ConfirmationDialog
        open={Boolean(archiveTargetPolicy)}
        onClose={handleArchiveDialogClose}
        Icon={CloseIcon}
        dialogTitle="Are you sure you want to archive the policy?"
        dialogBody={`This action will archive policy: ${archiveTargetPolicy?.name || "Unnamed Policy"}${
          archiveTargetPolicy?.source_name?.name ? ` for ${archiveTargetPolicy.source_name.name}` : ""
        }${archiveTargetPolicy?.policy_type === "provider" ? `. ${PROVIDER_ARCHIVE_CASCADE_NOTICE}` : ""}`}
        confirmationText="Yes, I want to archive the policy"
        handleConfirm={handleArchiveConfirm}
        isLoading={policyArchiving}
        dataTestId="archive-policy-confirmation-dialog"
      />

      <Box>
        {isLoading ? (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "40%",
            }}
          >
            <Loader />
          </Box>
        ) : policyEngines?.data && filters?.restaurant_group_id ? (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="policy-engine"
            type="entries"
            sortMode="server"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={policyEngines?.pageCount}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={!policyEngines?.data?.length || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        ) : (
          <Alert severity="info">Please select a restaurant group</Alert>
        )}
      </Box>
    </RestaurantGroupContainer>
  );
};
